/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  BOOKING_REQUEST: 'transition/booking-request',
  INQUIRE: 'transition/inquire',
  BOOKING_REQUEST_AFTER_INQUIRE: 'transition/booking-request-after-inquiry',
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',
  EXPIRE: 'transition/expire',
  OPERATOR_ACCEPTED_CANCEL: 'transition/operator-accepted-cancel',
  ACCEPTED_EXPIRE: 'transition/accepted-expire',
  CARD_PAYMENT: 'transition/card-payment',
  CONFIRM_PAYMENT: 'transition/confirm-card-payment',
  CAPTURE_CARD_PAYMENT: 'transition/capture-card-payment',
  INSTANT_PAYMENT: 'transition/instant-payment',
  CAPTURE_INSTANT_PAYMENT: 'transition/capture-instant-payment',
  PAYMENT_CARD_FAIL: 'transition/payment-card-fail',
  PAYMENT_INSTANT_FAIL: 'transition/payment-instant-fail',
  DISPUTE: 'transition/dispute',
  PROVIDER_DISPUTE: 'transition/provider-dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',
  RESOLVE_FULL_REFUND: 'transition/resolve-full-refund',
  RESOLVE_FULL_PAYOUT: 'transition/resolve-full-payout',
  RESOLVE_PARTIAL_REFUND: 'transition/resolve-partial-refund',
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',
  CANCEL: 'transition/cancel',
  CANCEL_NO_REFUND: 'transition/cancel-no-refund',
  CANCEL_PARTIAL_REFUND: 'transition/cancel-partial-refund',
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  ACCEPTED_EXPIRE_NOTIFY: 'transition/accepted-expire-notify',
  EXPIRE_NOTIFY_CARD_PAYMENT: 'transition/expire-notified-card-payment',
  EXPIRE_NOTIFY_INSTANT_PAYMENT: 'transition/expire-notified-instant-payment',
  PENDING_INSTANT_PAYMENT_EXPIRE: 'transition/pending-instant-payment-expire',
  PENDING_PAYMENT_EXPIRE: 'transition/pending-card-payment-expire',
  ACCEPTED_EXPIRE_AFTER_NOTIFY: 'transition/accepted-expire-after-notify',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  BOOKING_REQUESTED: 'booking-requested',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  PENDING_INSTANT_PAYMENT: 'pending-instant-payment',
  CAPTURED_PAYMENT: 'captured-payment',
  DISPUTED: 'disputed',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  EXPIRE_NOTIFIED: 'expire-notified',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.BOOKING_REQUEST]: states.BOOKING_REQUESTED,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.BOOKING_REQUEST_AFTER_INQUIRE]: states.BOOKING_REQUESTED,
      },
    },
    [states.BOOKING_REQUESTED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
      },
    },
    [states.ACCEPTED]: {
      on: {
        [transitions.OPERATOR_ACCEPTED_CANCEL]: states.CANCELED,
        [transitions.ACCEPTED_EXPIRE]: states.CANCELED,
        [transitions.CARD_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.INSTANT_PAYMENT]: states.PENDING_INSTANT_PAYMENT,
        [transitions.ACCEPTED_EXPIRE_NOTIFY]: states.EXPIRE_NOTIFIED,
      },
    },
    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
        [transitions.PAYMENT_CARD_FAIL]: states.PENDING_PAYMENT,
        [transitions.PENDING_PAYMENT_EXPIRE]: states.CANCELED,
      },
    },
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.CAPTURE_CARD_PAYMENT]: states.CAPTURED_PAYMENT,
      },
    },
    [states.PENDING_INSTANT_PAYMENT]: {
      on: {
        [transitions.CAPTURE_INSTANT_PAYMENT]: states.CAPTURED_PAYMENT,
        [transitions.PAYMENT_INSTANT_FAIL]: states.PENDING_INSTANT_PAYMENT,
        [transitions.PENDING_INSTANT_PAYMENT_EXPIRE]: states.CANCELED,
      },
    },
    [states.CAPTURED_PAYMENT]: {
      on: {
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.PROVIDER_DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
        [transitions.CANCEL]: states.CANCELED,
        [transitions.CANCEL_NO_REFUND]: states.CANCELED,
        [transitions.CANCEL_PARTIAL_REFUND]: states.CANCELED,
      },
    },
    [states.DISPUTED]: {
      on: {
        [transitions.RESOLVE_FULL_REFUND]: states.CANCELED,
        [transitions.RESOLVE_FULL_PAYOUT]: states.DELIVERED,
        [transitions.RESOLVE_PARTIAL_REFUND]: states.DELIVERED,
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
      },
    },
    [states.EXPIRE_NOTIFIED]: {
      on: {
        [transitions.EXPIRE_NOTIFY_CARD_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.EXPIRE_NOTIFY_INSTANT_PAYMENT]: states.PENDING_INSTANT_PAYMENT,
        [transitions.ACCEPTED_EXPIRE_AFTER_NOTIFY]: states.CANCELED,
      },
    },
    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },
    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.INQUIRE,
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.CANCEL_NO_REFUND,
    transitions.CANCEL_PARTIAL_REFUND,
    transitions.DISPUTE,
    transitions.PROVIDER_DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.RESOLVE_FULL_REFUND,
    transitions.RESOLVE_FULL_PAYOUT,
    transitions.RESOLVE_PARTIAL_REFUND,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.ACCEPTED_EXPIRE_NOTIFY,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.BOOKING_REQUEST,
    transitions.BOOKING_REQUEST_AFTER_INQUIRE,
    transitions.CAPTURE_INSTANT_PAYMENT,
    transitions.CAPTURE_CARD_PAYMENT,
    transitions.OPERATOR_ACCEPTED_CANCEL,
    transitions.ACCEPTED_EXPIRE_AFTER_NOTIFY,
    transitions.ACCEPTED_EXPIRE,
    transitions.PENDING_PAYMENT_EXPIRE,
    transitions.PENDING_INSTANT_PAYMENT_EXPIRE,
    transitions.EXPIRE_NOTIFY_CARD_PAYMENT,
    transitions.EXPIRE_NOTIFY_INSTANT_PAYMENT,
    transitions.PAYMENT_CARD_FAIL,
    transitions.PAYMENT_INSTANT_FAIL,
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.BOOKING_REQUEST_AFTER_INQUIRE, transitions.BOOKING_REQUEST].includes(
    transition
  );
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.RESOLVE_FULL_PAYOUT,
    transitions.RESOLVE_PARTIAL_REFUND,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.CANCEL_PARTIAL_REFUND,
    transitions.DECLINE,
    transitions.RESOLVE_FULL_REFUND,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.BOOKING_REQUESTED];
